import {
  IHouseStepAvailability,
  IWizardHouseState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import { Form, DatePicker } from "antd";
import { StepViewOnlyValue } from "../../../Student/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createCheckIsReadonlyStepField,
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../utils";

import { HouseAPI } from "../../../../../API/houseApi";
import { formatToServerDate, parseServerDate } from "../../../../../utils/time";
import styled from "styled-components/macro";
import dayjs from "dayjs";
import { DATE_PICKER_DATE_FORMAT } from "../../../../../constants";

const statePath = WIZARD_HOUSE_STEPS_PATHS.Availability;
export const WizardStepAvailability: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IHouseStepAvailability>>(
      state[statePath] ?? {}
    );
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return HouseAPI.wizardSaveAvailability(
          state.house_id!,
          state.wizard_type!,
          values
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return HouseAPI.wizardSaveAvailabilityPartially(
          state.house_id!,
          state.wizard_type!,
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepAvailabilityView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const Description = styled.div`
  margin-bottom: 32px;
`;

const Root = styled.div`
  .ant-form {
    gap: 24px;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .ant-form-item {
    flex-grow: 1;
    margin-inline-end: 0;
  }

  .ant-form-item-control {
    width: 100% !important;

    .ant-picker {
      width: 100%;
    }
  }

  .ant-form-item-row {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const WizardStepAvailabilityView: TWizardStepComponentEditableView<IHouseStepAvailability> =
  React.memo(
    ({ errors, values, setValues, viewonly, readonly, enabledFields }) => {
      const { t } = useTranslation("house");

      const inputSize = "large";

      const isReadonly = createCheckIsReadonlyStepField(
        readonly,
        enabledFields
      );

      return (
        <Root>
          <Description>
            {t(`wizard.steps.Availability.form.description`)}
          </Description>
          <Form layout="inline">
            <Form.Item
              label={t(`wizard.steps.Availability.form.available_from.title`)}
              validateStatus={errors?.available_from ? "error" : ""}
              help={errors?.available_from ? t("wizard.errors.generic") : ""}
            >
              {viewonly ? (
                <StepViewOnlyValue>{values.available_from}</StepViewOnlyValue>
              ) : (
                <DatePicker
                  value={
                    values.available_from
                      ? dayjs(parseServerDate(values.available_from))
                      : undefined
                  }
                  placeholder={t(
                    `wizard.steps.Availability.form.available_from.placeholder`
                  )}
                  size={inputSize}
                  disabled={isReadonly("available_from")}
                  format={DATE_PICKER_DATE_FORMAT}
                  onChange={(date) => {
                    const resetAvailableTo =
                      values.available_to && date
                        ? date.startOf("day").toDate().getTime() >
                          parseServerDate(values.available_to).getTime()
                        : false;

                    setValues({
                      ...values,
                      available_from: date
                        ? formatToServerDate(date.toDate())
                        : null,
                      available_to: resetAvailableTo
                        ? null
                        : values.available_to
                    });
                  }}
                  disabledDate={(date) => {
                    if (
                      date.toDate().getTime() <=
                      dayjs(new Date()).startOf("day").toDate().getTime()
                    ) {
                      return true;
                    }

                    return false;
                  }}
                />
              )}
            </Form.Item>
            <Form.Item
              label={t(`wizard.steps.Availability.form.available_to.title`)}
              validateStatus={errors?.available_to ? "error" : ""}
              help={
                errors?.available_to
                  ? t("wizard.errors.generic")
                  : t("wizard.steps.Availability.form.available_to.hint")
              }
            >
              {viewonly ? (
                <StepViewOnlyValue>
                  {values.available_to ?? ""}
                </StepViewOnlyValue>
              ) : (
                <DatePicker
                  value={
                    values.available_to
                      ? dayjs(parseServerDate(values.available_to))
                      : undefined
                  }
                  placeholder={t(
                    `wizard.steps.Availability.form.available_to.placeholder`
                  )}
                  size={inputSize}
                  disabled={isReadonly("available_to")}
                  format={DATE_PICKER_DATE_FORMAT}
                  onChange={(date) => {
                    setValues({
                      ...values,
                      available_to: date
                        ? formatToServerDate(date.toDate())
                        : null
                    });
                  }}
                  disabledDate={(date) => {
                    if (
                      (values.available_from &&
                        date.toDate().getTime() <
                          parseServerDate(values.available_from).getTime()) ||
                      date.toDate().getTime() <=
                        dayjs(new Date()).startOf("day").toDate().getTime()
                    ) {
                      return true;
                    }

                    return false;
                  }}
                />
              )}
            </Form.Item>
          </Form>
        </Root>
      );
    }
  );
